import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Stack } from "@mui/system";
import { Toast } from "./Toast";
import { useUser } from "../context/UserContext";
import { usePostHog } from "@posthog/react";
import { isFirebaseConfigured } from "../auth/firebaseClient";
import {
  DISPLAY_NAME_PROFANITY_MESSAGE,
  displayNameContainsProfanity,
} from "../utils/displayNamePolicy";

type Props = {
  titleText: string;
  buttonText: string;
  onFormSubmit: (value: string) => void;
  /** Shown under the title (e.g. invite-link join flow). */
  subtitleText?: string;
  /** Landing mode mirrors the PattaAdda entry screen. */
  mode?: "landing" | "name";
  /** `/join/...` without app JWT: name field + continue as guest + optional Google sign-in. */
  guestInviteMode?: boolean;
  /** Shown after an invite link join failed (e.g. room does not exist). */
  inviteLinkErrorText?: string | null;
};

export const Login = ({
  titleText,
  buttonText,
  onFormSubmit,
  subtitleText,
  mode = "name",
  guestInviteMode = false,
  inviteLinkErrorText = null,
}: Props) => {
  const posthog = usePostHog();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    user,
    signInWithEmailPassword,
    signUpWithEmailPassword,
    signInWithGoogle,
    sendPasswordResetEmail,
    signOut,
  } = useUser();
  const [value, setValue] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupDisplayName, setSignupDisplayName] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetDialogError, setResetDialogError] = useState<string | null>(null);

  const isStrongPassword = (pw: string): boolean => {
    if (pw.length < 8) return false;
    const hasUpper = /[A-Z]/.test(pw);
    const hasLower = /[a-z]/.test(pw);
    const hasDigit = /\d/.test(pw);
    const hasSpecial = /[^A-Za-z0-9]/.test(pw);
    return hasUpper && hasLower && hasDigit && hasSpecial;
  };

  const mapAuthError = (e: unknown, mode: "login" | "signup"): string => {
    const raw = e instanceof Error ? e.message : "Authentication failed";
    if (raw.includes("auth/invalid-email")) return "Please enter a valid email address.";
    if (raw.includes("auth/missing-password")) return "Please enter your password.";
    if (raw.includes("auth/invalid-credential")) return "Email or password is incorrect.";
    if (raw.includes("auth/user-not-found")) return "No account found for this email.";
    if (raw.includes("auth/wrong-password")) return "Email or password is incorrect.";
    if (raw.includes("auth/email-already-in-use")) return "An account with this email already exists. Try Login.";
    if (raw.includes("auth/weak-password")) return "Password is too weak. Use 8+ chars with mixed complexity.";
    if (raw.includes("auth/popup-closed-by-user")) return "Sign-in popup was closed. Please try again.";
    if (raw.includes("auth/network-request-failed")) return "Network issue. Check connection and try again.";
    if (raw.includes("not allowed")) return DISPLAY_NAME_PROFANITY_MESSAGE;
    return mode === "signup" ? "Could not create account. Please check details and try again." : "Could not log in. Please check your credentials and try again.";
  };

  const mapPasswordResetError = (e: unknown): string => {
    const raw = e instanceof Error ? e.message : "Something went wrong";
    if (raw.includes("auth/invalid-email")) return "Please enter a valid email address.";
    if (raw.includes("auth/missing-email")) return "Please enter your email address.";
    if (raw.includes("auth/too-many-requests")) return "Too many attempts. Wait a few minutes and try again.";
    if (raw.includes("auth/network-request-failed")) return "Network issue. Check connection and try again.";
    return "Could not send reset email. Try again later.";
  };

  const normalizedEmail = email.trim();
  const loginFieldsValid = normalizedEmail.length > 0 && password.length > 0;
  const signupNickProfane =
    signupDisplayName.trim().length > 0 && displayNameContainsProfanity(signupDisplayName);
  const signupFieldsValid =
    signupDisplayName.trim().length > 0 &&
    !signupNickProfane &&
    normalizedEmail.length > 0 &&
    password.length > 0 &&
    confirmPassword.length > 0 &&
    isStrongPassword(password) &&
    password === confirmPassword;

  const guestNameProfane = guestInviteMode && value.trim().length > 0 && displayNameContainsProfanity(value);
  const guestContinueValid =
    guestInviteMode && value.trim().length > 0 && !displayNameContainsProfanity(value.trim());

  useEffect(() => {
    if (user?.displayName) {
      setValue((v) => (v.trim().length === 0 ? user.displayName : v));
    }
  }, [user?.displayName]);

  const onClick = () => {
    if (guestInviteMode && !user) {
      const n = value.trim();
      if (!n) {
        setAuthError("Enter the name others will see at the table.");
        return;
      }
      if (displayNameContainsProfanity(n)) {
        setAuthError(DISPLAY_NAME_PROFANITY_MESSAGE);
        return;
      }
      setAuthError(null);
      posthog?.capture("guest_invite_continue_as_guest", { name_len: n.length });
      onFormSubmit(n);
      return;
    }
    if (!user) {
      setAuthError("Please sign in first.");
      return;
    }
    const finalName = user.displayName?.trim() || value.trim();
    if (!finalName) {
      setAuthError("Could not determine a display name. Update profile and try again.");
      return;
    }
    posthog?.capture("table_name_auto_from_profile", { display_name: finalName });
    onFormSubmit(finalName);
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: { xs: "flex-start", sm: "center" },
        minHeight: "100dvh",
        px: { xs: 1, sm: 2 },
        py: { xs: 1.5, sm: 3 },
        pb: { xs: "max(16px, env(safe-area-inset-bottom, 0px))", sm: 3 },
        boxSizing: "border-box",
        overflowY: "auto",
      }}
    >
      <Paper
        elevation={24}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: { xs: 420, sm: 560, md: 620 },
          borderRadius: { xs: 2, sm: 3 },
        }}
      >
        <Stack spacing={{ xs: 1.25, sm: 2 }} sx={{ width: "100%", padding: { xs: 2, sm: 4, md: 6 } }}>
          <Typography
            color="primary"
            variant="h5"
            sx={{
              fontSize: { xs: "1.9rem", sm: "2.2rem", md: "2.5rem" },
              fontWeight: 800,
              textAlign: "center",
              lineHeight: 1.1,
              background: "linear-gradient(90deg, #72d572 0%, #ffca28 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {titleText}
          </Typography>
          {subtitleText ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", fontSize: { xs: "0.82rem", sm: "0.9rem" } }}
            >
              {subtitleText}
            </Typography>
          ) : null}

          {inviteLinkErrorText ? (
            <Alert severity="warning" sx={{ textAlign: "left" }}>
              {inviteLinkErrorText}
            </Alert>
          ) : null}

          {guestInviteMode ? (
            <TextField
              fullWidth
              color="info"
              name="guestTableName"
              label="Your name at the table"
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              error={Boolean(guestNameProfane)}
              helperText={
                guestNameProfane
                  ? DISPLAY_NAME_PROFANITY_MESSAGE
                  : "Others in the room will see this name."
              }
            />
          ) : null}

          {isFirebaseConfigured ? (
            <>
              <ToggleButtonGroup
                color="primary"
                value={authMode}
                exclusive
                fullWidth
                onChange={(_e, next) => {
                  if (next) setAuthMode(next);
                }}
                sx={{ mb: 0.5, "& .MuiToggleButton-root": { py: { xs: 0.65, sm: 0.85 } } }}
              >
                <ToggleButton value="login">Login</ToggleButton>
                <ToggleButton value="signup">Sign up</ToggleButton>
              </ToggleButtonGroup>
              {authMode === "signup" ? (
                <TextField
                  fullWidth
                  color="info"
                  name="displayName"
                  label="Nickname"
                  type="text"
                  value={signupDisplayName}
                  onChange={(e) => setSignupDisplayName(e.target.value)}
                  error={signupNickProfane}
                  helperText={signupNickProfane ? DISPLAY_NAME_PROFANITY_MESSAGE : "Letters and numbers; no offensive language."}
                />
              ) : null}
              <TextField
                fullWidth
                color="info"
                name="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                fullWidth
                color="info"
                name="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText={
                  authMode === "signup"
                    ? "Use 8+ chars with upper, lower, number, and symbol."
                    : undefined
                }
              />
              {authMode === "login" ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setResetEmail(normalizedEmail);
                      setResetSent(false);
                      setResetDialogError(null);
                      setForgotOpen(true);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              ) : null}
              {authMode === "signup" ? (
                <TextField
                  fullWidth
                  color="info"
                  name="confirmPassword"
                  label="Confirm password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              ) : null}
              <Button
                fullWidth
                variant="contained"
                disabled={authMode === "login" ? !loginFieldsValid : !signupFieldsValid}
                sx={{ py: { xs: 1.15, sm: 1.3 }, fontWeight: 700 }}
                onClick={async () => {
                  setAuthError(null);
                  try {
                    if (authMode === "login") {
                      if (!loginFieldsValid) {
                        throw new Error("Please enter both email and password.");
                      }
                      await signInWithEmailPassword(email, password);
                      return;
                    }
                    if (!signupDisplayName.trim()) {
                      throw new Error("Nickname is required for sign up");
                    }
                    if (!isStrongPassword(password)) {
                      throw new Error(
                        "Password must be 8+ chars and include uppercase, lowercase, number, and symbol."
                      );
                    }
                    if (password !== confirmPassword) {
                      throw new Error("Passwords do not match");
                    }
                    if (displayNameContainsProfanity(signupDisplayName.trim())) {
                      throw new Error(DISPLAY_NAME_PROFANITY_MESSAGE);
                    }
                    await signUpWithEmailPassword(email, password, signupDisplayName.trim());
                  } catch (e) {
                    setAuthError(mapAuthError(e, authMode));
                  }
                }}
              >
                {authMode === "login" ? "Login" : "Create account"}
              </Button>
              <Stack direction={compact ? "column" : "row"} spacing={1}>
                <Button fullWidth variant="outlined" startIcon={<GoogleIcon />} sx={{ py: { xs: 1.05, sm: 1.1 } }} onClick={async () => {
                  setAuthError(null);
                  try { await signInWithGoogle(); } catch (e) { setAuthError(mapAuthError(e, "login")); }
                }}>Google</Button>
              </Stack>
              {user ? (
                <Button size="small" onClick={() => signOut()} sx={{ alignSelf: "center" }}>
                  Sign out ({user.displayName})
                </Button>
              ) : null}
            </>
          ) : null}

          <Button
            fullWidth
            onClick={() => onClick()}
            variant="contained"
            color="secondary"
            disabled={guestInviteMode ? !guestContinueValid : !user}
            sx={
              mode === "landing"
                ? {
                    minHeight: 52,
                    fontWeight: 800,
                    fontSize: "1.02rem",
                    backgroundColor: "#3b7a3f",
                    color: "#f3f6f4",
                    "&:hover": {
                      backgroundColor: "#2f6633",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(59, 122, 63, 0.35)",
                      color: "rgba(243, 246, 244, 0.65)",
                    },
                  }
                : undefined
            }
          >
            {buttonText}
          </Button>
          {!isFirebaseConfigured && user ? (
            <Button size="small" onClick={() => signOut()}>
              Sign out ({user.displayName})
            </Button>
          ) : null}
        </Stack>
      </Paper>
      {authError ? (
        <Toast severity="error" message={authError} onClose={() => setAuthError(null)} />
      ) : null}

      <Dialog
        open={forgotOpen}
        onClose={() => {
          if (!resetSending) setForgotOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="forgot-password-title"
      >
        <DialogTitle id="forgot-password-title">Reset password</DialogTitle>
        <DialogContent>
          {resetSent ? (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              If an account exists for that email, we sent a link to reset your password. Check your inbox
              and spam folder, then return here to sign in.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pt: 0.5 }}>
                Enter the email you used to create your account. We will send a link to reset your password—check
                spam or junk if you do not see it within a few minutes.
              </Typography>
              <TextField
                fullWidth
                autoFocus
                label="Email"
                type="email"
                value={resetEmail}
                onChange={(e) => {
                  setResetEmail(e.target.value);
                  setResetDialogError(null);
                }}
                error={Boolean(resetDialogError)}
                helperText={resetDialogError || undefined}
                margin="dense"
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {resetSent ? (
            <Button onClick={() => setForgotOpen(false)} variant="contained" fullWidth>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={() => setForgotOpen(false)} disabled={resetSending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={resetSending || !resetEmail.trim()}
                onClick={async () => {
                  setResetDialogError(null);
                  setResetSending(true);
                  try {
                    await sendPasswordResetEmail(resetEmail);
                    setResetSent(true);
                  } catch (e: unknown) {
                    const code =
                      typeof e === "object" && e !== null && "code" in e
                        ? String((e as { code: string }).code)
                        : "";
                    if (code === "auth/user-not-found") {
                      setResetSent(true);
                    } else {
                      setResetDialogError(mapPasswordResetError(e));
                    }
                  } finally {
                    setResetSending(false);
                  }
                }}
              >
                {resetSending ? "Sending…" : "Send reset link"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
